import api from './api';

export interface WhatsAppAccount {
  _id: string;
  userId: string;
  accountName: string;
  phoneNumber: string;
  sessionId: string;
  isActive: boolean;
  isConnected: boolean;
  isReady: boolean;
  lastActivity?: Date;
  sessionInfo?: any;
  qrCode?: string;
  groups: WhatsAppGroup[];
  settings: {
    autoAcceptRides: boolean;
    monitoredGroups: string[];
    messageFilters: {
      keywords: string[];
      excludeKeywords: string[];
      minConfidence: number;
    };
  };
  createdAt: Date;
  status?: {
    sessionId: string;
    accountName: string;
    phoneNumber: string;
    isConnected: boolean;
    isReady: boolean;
    qrCode?: string;
    lastActivity?: Date;
    sessionInfo?: any;
    groups: WhatsAppGroup[];
    settings: any;
  };
}

export interface WhatsAppGroup {
  id: string;
  name: string;
  participantCount: number;
  lastActivity: Date;
  isActive: boolean;
}

export interface CreateAccountData {
  accountName: string;
  phoneNumber: string;
}

export const whatsappAccountService = {
  // Get all WhatsApp accounts for the user
  async getAccounts(): Promise<WhatsAppAccount[]> {
    const response = await api.get('/whatsapp-accounts');
    return response.data;
  },

  // Create a new WhatsApp account
  async createAccount(data: CreateAccountData): Promise<WhatsAppAccount> {
    const response = await api.post('/whatsapp-accounts', data);
    return response.data;
  },

  // Connect/initialize an account
  async connectAccount(sessionId: string): Promise<{ message: string }> {
    const response = await api.post(`/whatsapp-accounts/${sessionId}/connect`);
    return response.data;
  },

  // Disconnect an account
  async disconnectAccount(sessionId: string): Promise<{ message: string }> {
    const response = await api.post(`/whatsapp-accounts/${sessionId}/disconnect`);
    return response.data;
  },

  // Get account status
  async getAccountStatus(sessionId: string): Promise<any> {
    const response = await api.get(`/whatsapp-accounts/${sessionId}/status`);
    return response.data;
  },

  // Get groups for an account
  async getAccountGroups(sessionId: string): Promise<WhatsAppGroup[]> {
    const response = await api.get(`/whatsapp-accounts/${sessionId}/groups`);
    return response.data;
  },

  // Update account settings
  async updateAccountSettings(sessionId: string, settings: any): Promise<WhatsAppAccount> {
    const response = await api.put(`/whatsapp-accounts/${sessionId}/settings`, settings);
    return response.data;
  },

  // Send message from specific account
  async sendMessage(sessionId: string, number: string, message: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/whatsapp-accounts/${sessionId}/send-message`, {
      number,
      message
    });
    return response.data;
  },

  // Update account details
  async updateAccount(sessionId: string, data: Partial<CreateAccountData>): Promise<WhatsAppAccount> {
    const response = await api.put(`/whatsapp-accounts/${sessionId}`, data);
    return response.data;
  },

  // Reinitialize account (reconnect without QR scan if session is still valid)
  async reinitializeAccount(sessionId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/whatsapp-accounts/${sessionId}/reinitialize`);
    return response.data;
  },

  // Delete account
  async deleteAccount(sessionId: string): Promise<{ message: string }> {
    const response = await api.delete(`/whatsapp-accounts/${sessionId}`);
    return response.data;
  }
};

export default whatsappAccountService;
